import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import {
    Button,
    Stack,
    Box,
    Typography,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Grid,
    Alert,
    IconButton,
    Drawer,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Tooltip,
    DialogActions,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Tabs,
    Tab,
    InputAdornment
} from '@mui/material';
import { ArrowBack, CheckCircle, Error, HourglassEmpty, Business, Description, Visibility, ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import { showToast, showErrorToast as showError } from 'src/utils/Toast/SnackbarProvider';

import Page from 'src/components/Page';
import { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import AddCompany from '../company/AddCompany';

const statusInfo = {
    'pending': { label: 'Pending', color: 'default', icon: <HourglassEmpty />, progress: 0 },
    'parsing': { label: 'Parsing Prompt', color: 'info', icon: <HourglassEmpty />, progress: 10 },
    'searching': { label: 'Searching Companies', color: 'info', icon: <Business />, progress: 30 },
    'creating': { label: 'Creating Companies', color: 'warning', icon: <Business />, progress: 50 },
    'generating_pitches': { label: 'Generating Pitches', color: 'warning', icon: <Description />, progress: 75 },
    'completed': { label: 'Completed', color: 'success', icon: <CheckCircle />, progress: 100 },
    'failed': { label: 'Failed', color: 'error', icon: <Error />, progress: 100 },
    'no_new_companies': { label: 'All Duplicates', color: 'warning', icon: <Error />, progress: 100 }
};

const statusEducation = {
    'pending': {
        title: '⏳ Getting Ready...',
        description: 'Your request is queued and will start processing shortly.',
        steps: ['Initializing enrichment engine', 'Preparing search parameters'],
        tip: 'This usually takes just a few seconds.'
    },
    'parsing': {
        title: '🔍 Understanding Your Prompt',
        description: 'Our AI is analyzing your search criteria to find the best matches.',
        steps: ['Extracting key requirements', 'Identifying industry filters', 'Setting up location parameters', 'Configuring company size filters'],
        tip: 'We\'re translating your natural language into precise search parameters.'
    },
    'searching': {
        title: '🌐 Searching Global Database',
        description: 'Scanning millions of companies to find your perfect matches.',
        steps: ['Querying company databases', 'Filtering by your criteria', 'Ranking by relevance', 'Removing duplicates'],
        tip: 'We search across multiple data sources to ensure comprehensive results.'
    },
    'creating': {
        title: '🏢 Creating & Validating Companies',
        description: 'This is the most thorough step - we\'re carefully validating each company.',
        steps: [
            '✓ Checking if company already exists in your database',
            '✓ Verifying company meets your criteria',
            '✓ Enriching with additional data (funding, employees, etc.)',
            '✓ Validating contact information',
            '✓ Checking company status (active/inactive)'
        ],
        tip: 'Quality over speed! We ensure each company is worth your outreach time.',
        isLongRunning: true
    },
    'generating_pitches': {
        title: '✍️ Crafting Personalized Pitches',
        description: 'Our AI is writing custom pitches for each company.',
        steps: ['Analyzing company profile', 'Matching with your pitch template', 'Personalizing messaging', 'Optimizing for engagement'],
        tip: 'Each pitch is uniquely crafted based on the company\'s specific details.'
    },
    'completed': {
        title: '🎉 All Done!',
        description: 'Your companies are ready for outreach.',
        steps: [],
        tip: 'Click on any company to view details and mark as outreached.'
    },
    'failed': {
        title: '❌ Something Went Wrong',
        description: 'We encountered an issue during processing.',
        steps: [],
        tip: 'Check the error message below and try again.'
    },
    'no_new_companies': {
        title: '📋 No New Companies Found',
        description: 'All companies from this search already exist in your database.',
        steps: [],
        tip: 'Try adjusting your search criteria or expanding to new regions.'
    }
};

const ProcessingStepsIndicator = ({ status, currentStep, progress }) => {
    const education = statusEducation[status];
    if (!education || education.steps.length === 0) return null;
    
    const isInProgress = !['completed', 'failed', 'no_new_companies'].includes(status);
    
    // Get real-time progress data
    const progressData = progress || {};
    const rejectionCounts = progressData.rejection_counts || {};
    const companiesChecked = progressData.companies_checked || 0;
    const companiesCreated = progressData.companies_created || 0;
    const currentCompany = progressData.current_company;
    
    // Calculate total rejections
    const totalRejections = Object.values(rejectionCounts).reduce((sum, count) => sum + count, 0);
    
    // Build real-time steps for 'creating' status
    const getRealTimeSteps = () => {
        if (status !== 'creating' || companiesChecked === 0) {
            return education.steps;
        }
        
        const steps = [];
        steps.push(`✓ Checked ${companiesChecked} companies from Apollo`);
        
        if (companiesCreated > 0) {
            steps.push(`✓ Created ${companiesCreated} valid companies`);
        }
        
        if (totalRejections > 0) {
            steps.push(`⚠️ Rejected ${totalRejections} companies:`);
            if (rejectionCounts.duplicate > 0) steps.push(`   • ${rejectionCounts.duplicate} duplicates`);
            if (rejectionCounts.duplicate_marked_invalid > 0) steps.push(`   • ${rejectionCounts.duplicate_marked_invalid} already marked invalid`);
            if (rejectionCounts.duplicate_outreached > 0) steps.push(`   • ${rejectionCounts.duplicate_outreached} already outreached`);
            if (rejectionCounts.duplicate_deactivated > 0) steps.push(`   • ${rejectionCounts.duplicate_deactivated} deactivated`);
            if (rejectionCounts.no_website > 0) steps.push(`   • ${rejectionCounts.no_website} no website`);
            if (rejectionCounts.website_not_working > 0) steps.push(`   • ${rejectionCounts.website_not_working} website not working`);
            if (rejectionCounts.business_type_mismatch > 0) steps.push(`   • ${rejectionCounts.business_type_mismatch} not software/digital product`);
            if (rejectionCounts.low_confidence > 0) steps.push(`   • ${rejectionCounts.low_confidence} low confidence`);
            if (rejectionCounts.insufficient_evidence > 0) steps.push(`   • ${rejectionCounts.insufficient_evidence} website unclear`);
            if (rejectionCounts.employee_size_mismatch > 0) steps.push(`   • ${rejectionCounts.employee_size_mismatch} wrong employee size`);
        }
        
        if (currentCompany) {
            steps.push(`🔄 Currently validating: ${currentCompany}`);
        }
        
        return steps;
    };
    
    const displaySteps = getRealTimeSteps();
    
    return (
        <Box sx={{ 
            p: 2, 
            mb: 2, 
            borderRadius: 2,
            background: education.isLongRunning 
                ? 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)' 
                : 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
            border: '1px solid',
            borderColor: education.isLongRunning ? 'warning.light' : 'info.light'
        }}>
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                        {education.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {education.description}
                    </Typography>
                </Box>
                
                {displaySteps.length > 0 && (
                    <Box sx={{ pl: 1 }}>
                        {displaySteps.map((step, index) => (
                            <Stack 
                                key={index} 
                                direction="row" 
                                spacing={1} 
                                alignItems="center"
                                sx={{ 
                                    py: 0.3,
                                    opacity: 1
                                }}
                            >
                                {step.startsWith('🔄') ? (
                                    <CircularProgress size={12} thickness={5} />
                                ) : step.startsWith('   •') ? (
                                    <Box sx={{ width: 12 }} />
                                ) : (
                                    <Box sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        bgcolor: step.startsWith('✓') ? 'success.main' : step.startsWith('⚠️') ? 'warning.main' : 'grey.300',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '8px',
                                        color: 'white'
                                    }}>
                                        {step.startsWith('✓') && '✓'}
                                    </Box>
                                )}
                                <Typography variant="caption" sx={{ 
                                    fontSize: step.startsWith('   •') ? '0.7rem' : '0.75rem',
                                    color: step.startsWith('   •') ? 'text.secondary' : 'inherit',
                                    fontWeight: step.startsWith('🔄') ? 500 : 400
                                }}>
                                    {step.replace('✓ ', '').replace('⚠️ ', '').replace('🔄 ', '')}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                )}
                
                {education.tip && !companiesChecked && (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 0.5,
                        p: 1,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        borderRadius: 1
                    }}>
                        <Typography sx={{ fontSize: '0.8rem' }}>💡</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                            {education.tip}
                        </Typography>
                    </Box>
                )}
                
                {education.isLongRunning && isInProgress && (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1,
                        bgcolor: 'rgba(255,152,0,0.1)',
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'warning.main'
                    }}>
                        <Typography sx={{ fontSize: '1rem' }}>☕</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'warning.dark', fontWeight: 500 }}>
                            This step takes a bit longer — we're being thorough! Grab a coffee, we've got this.
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default function EnrichmentProgress({ runHistoryIdProp, onClose, isDrawerMode = false, onExpandChange }) {
    const { runHistoryId: runHistoryIdParam } = useParams();
    const navigate = useNavigate();
    
    // Use prop if provided (drawer mode), otherwise use URL param (page mode)
    const runHistoryId = runHistoryIdProp || runHistoryIdParam;
    
    const [runHistory, setRunHistory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [processingStep, setProcessingStep] = useState(0);
    
    // Rejected companies
    const [rejectedCompanies, setRejectedCompanies] = useState([]);
    const [rejectedStats, setRejectedStats] = useState({});
    const [loadingRejected, setLoadingRejected] = useState(false);
    const [showRejected, setShowRejected] = useState(false);
    const [rejectedTab, setRejectedTab] = useState(0); // 0=Duplicates, 1=All Others
    const [rejectedSearch, setRejectedSearch] = useState(''); // Search across both tabs
    const [duplicateFilter, setDuplicateFilter] = useState('all'); // 'all', 'duplicate', 'duplicate_outreached', 'duplicate_marked_invalid', 'duplicate_deactivated'
    const [rejectedFilter, setRejectedFilter] = useState('all'); // 'all', 'no_website', 'website_not_working', 'business_type_mismatch', 'low_confidence', 'insufficient_evidence', 'employee_size_mismatch'
    const [selectedRejectedCompany, setSelectedRejectedCompany] = useState(null); // For viewing rejected company details
    const [duplicateCompanyId, setDuplicateCompanyId] = useState(null); // For loading existing company for duplicates
    
    // Company detail - show side-by-side
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companyDetails, setCompanyDetails] = useState({});
    const [loadingCompanies, setLoadingCompanies] = useState({});
    
    // Mark Outreach state - now as side panel instead of dialog
    const [markOutreachPanelOpen, setMarkOutreachPanelOpen] = useState(false);
    const [outreachCompanyId, setOutreachCompanyId] = useState(null);
    const [outreachMethod, setOutreachMethod] = useState('email');
    const [outreachComment, setOutreachComment] = useState('');
    const [outreachDate, setOutreachDate] = useState('');
    const [outreachTime, setOutreachTime] = useState('');
    const [isMarkingOutreach, setIsMarkingOutreach] = useState(false);
    const [existingOutreachDetails, setExistingOutreachDetails] = useState(null);
    const [loadingOutreachDetails, setLoadingOutreachDetails] = useState(false);
    
    // Load More state
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    // Mark Invalid state
    const [isMarkingInvalid, setIsMarkingInvalid] = useState(false);
    const [markInvalidPanelOpen, setMarkInvalidPanelOpen] = useState(false);
    const [invalidCompanyId, setInvalidCompanyId] = useState(null);
    const [invalidReasons, setInvalidReasons] = useState([]);
    const [selectedInvalidReason, setSelectedInvalidReason] = useState('');
    const [invalidDescription, setInvalidDescription] = useState('');
    const [loadingInvalidReasons, setLoadingInvalidReasons] = useState(false);
    
    // Notify parent when expansion state changes (either company detail or mark panel or rejected company or invalid panel)
    useEffect(() => {
        if (onExpandChange) {
            onExpandChange(!!selectedCompanyId || markOutreachPanelOpen || !!selectedRejectedCompany || markInvalidPanelOpen);
        }
    }, [selectedCompanyId, markOutreachPanelOpen, selectedRejectedCompany, markInvalidPanelOpen, onExpandChange]);
    
    // State for searching duplicate company
    const [searchingDuplicate, setSearchingDuplicate] = useState(false);
    
    // Ref to track current search and prevent race conditions
    const currentSearchRef = useRef(null);
    
    // Helper function to search for duplicate company by name
    const searchForDuplicateCompany = async (company, searchingForCompanyId) => {
        // Normalize website for comparison
        const normalizeUrl = (url) => {
            if (!url) return '';
            return url.toLowerCase().replace('https://', '').replace('http://', '').replace('www.', '').replace(/\/$/, '');
        };
        
        const response = await apiInstance.get(`company/?search=${encodeURIComponent(company.company_name)}&page_size=10`);
        const results = response?.data?.results?.data || response?.data?.data || [];
        
        // Check if user has clicked another company while we were searching
        if (currentSearchRef.current !== searchingForCompanyId) {
            return null; // Abort - user clicked another company
        }
        
        const rejectedWebsite = normalizeUrl(company.company_website);
        
        // Find match - prioritize exact name + website match
        let exactMatch = null;
        
        if (rejectedWebsite) {
            // If we have a website, match by website (most reliable)
            exactMatch = results.find(c => {
                const companyWebsite = normalizeUrl(c.website);
                return companyWebsite && (
                    companyWebsite === rejectedWebsite ||
                    companyWebsite.includes(rejectedWebsite) ||
                    rejectedWebsite.includes(companyWebsite)
                );
            });
        }
        
        // If no website match, try exact name match only
        if (!exactMatch) {
            exactMatch = results.find(c => 
                c.company_name?.toLowerCase().trim() === company.company_name?.toLowerCase().trim()
            );
        }
        
        return exactMatch;
    };
    
    // Handler for viewing rejected company
    const handleViewRejectedCompany = async (company, e) => {
        if (e) e.stopPropagation();
        setSelectedCompanyId(null);
        setMarkOutreachPanelOpen(false);
        setSelectedRejectedCompany(company);
        setDuplicateCompanyId(null);
        
        // Store the company id we're searching for to prevent race conditions
        const searchingForCompanyId = company.id;
        currentSearchRef.current = searchingForCompanyId;
        
        // For duplicates, validate existing_company_id or search for it
        if (company.rejection_reason === 'duplicate') {
            setSearchingDuplicate(true);
            try {
                if (company.existing_company_id) {
                    // Validate that existing_company_id actually matches the rejected company
                    const validateResponse = await apiInstance.get(`company/${company.existing_company_id}/`);
                    const existingCompany = validateResponse?.data?.data;
                    
                    // Check if user clicked another company while validating
                    if (currentSearchRef.current !== searchingForCompanyId) {
                        return;
                    }
                    
                    // Check if the existing company name matches the rejected company name
                    const existingName = existingCompany?.company_name?.toLowerCase().trim();
                    const rejectedName = company.company_name?.toLowerCase().trim();
                    
                    if (existingName === rejectedName) {
                        // Names match - use the existing_company_id
                        setDuplicateCompanyId(company.existing_company_id);
                    } else {
                        // Names don't match - existing_company_id is wrong, search instead
                        console.warn(`existing_company_id mismatch: expected "${company.company_name}" but got "${existingCompany?.company_name}". Searching...`);
                        const exactMatch = await searchForDuplicateCompany(company, searchingForCompanyId);
                        if (currentSearchRef.current === searchingForCompanyId && exactMatch) {
                            setDuplicateCompanyId(exactMatch.id);
                        }
                    }
                } else {
                    // No existing_company_id - search for company by name
                    const exactMatch = await searchForDuplicateCompany(company, searchingForCompanyId);
                    if (currentSearchRef.current === searchingForCompanyId && exactMatch) {
                        setDuplicateCompanyId(exactMatch.id);
                    }
                }
            } catch (error) {
                console.error('Failed to validate/search duplicate company:', error);
            } finally {
                // Only clear searching state if still on the same company
                if (currentSearchRef.current === searchingForCompanyId) {
                    setSearchingDuplicate(false);
                }
            }
        }
    };
    
    const handleCloseRejectedCompany = () => {
        setSelectedRejectedCompany(null);
        setDuplicateCompanyId(null);
    };
    
    // Fetch company details
    const fetchCompanyDetails = async (companyId) => {
        if (companyDetails[companyId] || loadingCompanies[companyId]) return;
        
        setLoadingCompanies(prev => ({ ...prev, [companyId]: true }));
        try {
            const response = await apiInstance.get(`company/${companyId}/`);
            const data = response?.data?.data;
            if (data) {
                setCompanyDetails(prev => ({ ...prev, [companyId]: data }));
            }
        } catch (error) {
            console.error('Failed to fetch company details:', error);
            setCompanyDetails(prev => ({ ...prev, [companyId]: { error: true } }));
        } finally {
            setLoadingCompanies(prev => ({ ...prev, [companyId]: false }));
        }
    };
    
    // Fetch all company details when run history loads
    useEffect(() => {
        if (runHistory?.companies_created?.length > 0) {
            runHistory.companies_created.forEach(id => fetchCompanyDetails(id));
        }
    }, [runHistory?.companies_created]);
    
    // Fetch company details for ALL duplicate types to enable dynamic status check
    useEffect(() => {
        if (rejectedCompanies?.length > 0) {
            rejectedCompanies.forEach(company => {
                // Fetch for all duplicate types, not just 'duplicate'
                if (company.rejection_reason?.startsWith('duplicate') && company.existing_company_id) {
                    fetchCompanyDetails(company.existing_company_id);
                }
            });
        }
    }, [rejectedCompanies]);
    
    // Fetch stats for all rejection types (for tab counts)
    const fetchRejectedStats = async () => {
        if (!runHistoryId) return;
        try {
            // Fetch without rejection_reason filter to get stats for all types
            const response = await apiInstance.get(`enrichment-rejected-companies/?run_history_id=${runHistoryId}&page_size=1`);
            const results = response?.data?.results;
            if (results?.stats) {
                setRejectedStats(results.stats);
            }
        } catch (error) {
            console.error('Failed to fetch rejected stats:', error);
        }
    };
    
    // Fetch rejected companies by rejection type
    const fetchRejectedCompanies = async (tab, dupFilter = 'all', valFilter = 'all') => {
        if (!runHistoryId) return;
        setLoadingRejected(true);
        try {
            let url = `enrichment-rejected-companies/?run_history_id=${runHistoryId}&page_size=500`;
            
            if (tab === 0) {
                // Duplicates tab - filter by dropdown selection
                if (dupFilter === 'all') {
                    url += '&rejection_reason=duplicate,duplicate_marked_invalid,duplicate_outreached,duplicate_deactivated';
                } else {
                    url += `&rejection_reason=${dupFilter}`;
                }
            } else {
                // Others tab - filter by dropdown selection
                if (valFilter === 'all') {
                    // All non-duplicate rejections
                    url += '&rejection_reason=no_website,website_not_working,business_type_mismatch,low_confidence,insufficient_evidence,employee_size_mismatch';
                } else {
                    url += `&rejection_reason=${valFilter}`;
                }
            }
            
            const response = await apiInstance.get(url);
            const results = response?.data?.results;
            if (results) {
                setRejectedCompanies(results.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch rejected companies:', error);
        } finally {
            setLoadingRejected(false);
        }
    };
    
    // Fetch stats when showRejected is toggled on (for tab counts)
    useEffect(() => {
        if (showRejected) {
            fetchRejectedStats();
        }
    }, [showRejected, runHistoryId]);
    
    // Fetch rejected companies when tab or filter changes
    useEffect(() => {
        if (showRejected) {
            fetchRejectedCompanies(rejectedTab, duplicateFilter, rejectedFilter);
        }
    }, [showRejected, runHistoryId, rejectedTab, duplicateFilter, rejectedFilter]);
    
    const handleOpenCompanyDetail = (companyId) => {
        // Close mark panel if open
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        setSelectedCompanyId(companyId);
    };
    
    const handleCloseCompanyDetail = () => {
        setSelectedCompanyId(null);
    };
    
    // Mark Outreach handlers - opens as side panel
    const handleOpenMarkOutreach = async (companyId, e) => {
        if (e) e.stopPropagation();
        // Close company detail if open
        setSelectedCompanyId(null);
        setSelectedRejectedCompany(null);
        setOutreachCompanyId(companyId);
        setExistingOutreachDetails(null);
        setMarkOutreachPanelOpen(true);
        
        // Fetch existing outreach details
        setLoadingOutreachDetails(true);
        try {
            const response = await apiInstance.get(`company/${companyId}/outreach-details/`);
            if (response.data.success && response.data.data) {
                setExistingOutreachDetails(response.data.data);
            } else {
                // No existing outreach - show form with defaults
                const now = new Date();
                setOutreachMethod('email');
                setOutreachComment('');
                setOutreachDate(now.toISOString().split('T')[0]);
                setOutreachTime(now.toTimeString().slice(0, 5));
            }
        } catch (error) {
            // 404 means not outreached yet - show form
            const now = new Date();
            setOutreachMethod('email');
            setOutreachComment('');
            setOutreachDate(now.toISOString().split('T')[0]);
            setOutreachTime(now.toTimeString().slice(0, 5));
        } finally {
            setLoadingOutreachDetails(false);
        }
    };
    
    const handleCloseMarkOutreach = () => {
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        setExistingOutreachDetails(null);
    };
    
    const handleSubmitMarkOutreach = async () => {
        if (!outreachComment.trim()) {
            showErrorToast({ message: 'Comment is required' });
            return;
        }
        
        setIsMarkingOutreach(true);
        try {
            const outreachDateTime = new Date(`${outreachDate}T${outreachTime}`);
            await apiInstance.post(`company/${outreachCompanyId}/mark-outreached/`, {
                method: outreachMethod,
                comment: outreachComment,
                outreach_datetime: outreachDateTime.toISOString()
            });
            showToast('Marked as outreached successfully!');
            handleCloseMarkOutreach();
            // Refresh company details
            setCompanyDetails(prev => {
                const updated = { ...prev };
                delete updated[outreachCompanyId];
                return updated;
            });
            fetchCompanyDetails(outreachCompanyId);
        } catch (error) {
            showErrorToast(error);
        } finally {
            setIsMarkingOutreach(false);
        }
    };
    
    // Invalid Company handlers
    const fetchInvalidReasons = async () => {
        setLoadingInvalidReasons(true);
        try {
            const response = await apiInstance.get('company/invalid-reasons/');
            if (response.data.success) {
                setInvalidReasons(response.data.data || []);
            } else if (response.data && Array.isArray(response.data)) {
                setInvalidReasons(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch invalid reasons:', error);
            showErrorToast('Failed to load invalid reasons');
        } finally {
            setLoadingInvalidReasons(false);
        }
    };
    
    const handleOpenMarkInvalid = async (companyId, e) => {
        if (e) e.stopPropagation();
        // Close other panels
        setSelectedCompanyId(null);
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        setSelectedRejectedCompany(null);
        
        setInvalidCompanyId(companyId);
        setSelectedInvalidReason('');
        setInvalidDescription('');
        await fetchInvalidReasons();
        setMarkInvalidPanelOpen(true);
    };
    
    const handleCloseMarkInvalid = () => {
        setMarkInvalidPanelOpen(false);
        setInvalidCompanyId(null);
        setSelectedInvalidReason('');
        setInvalidDescription('');
    };
    
    const handleSubmitMarkInvalid = async () => {
        if (!selectedInvalidReason) {
            showErrorToast('Please select a reason');
            return;
        }
        
        setIsMarkingInvalid(true);
        try {
            const response = await apiInstance.post(
                `company/${invalidCompanyId}/mark-invalid/`,
                {
                    reason_id: selectedInvalidReason,
                    description: invalidDescription.trim() || null
                }
            );
            
            if (response.data.success) {
                showToast('Company marked as invalid');
                handleCloseMarkInvalid();
                // Refresh company details
                try {
                    const refreshResponse = await apiInstance.get(`company/${invalidCompanyId}/`);
                    const refreshedData = refreshResponse?.data?.data;
                    if (refreshedData) {
                        setCompanyDetails(prev => ({ ...prev, [invalidCompanyId]: refreshedData }));
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh company:', refreshError);
                }
            }
        } catch (error) {
            console.error('Mark invalid error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to mark as invalid');
        } finally {
            setIsMarkingInvalid(false);
        }
    };

    const fetchRunHistory = () => {
        apiInstance.get(`enrichment-run-history/${runHistoryId}/`)
            .then((response) => {
                const data = response?.data?.data;
                setRunHistory(data);
                
                // Stop auto-refresh if completed or failed
                if (data?.status === 'completed' || data?.status === 'failed') {
                    setAutoRefresh(false);
                }
            })
            .catch((error) => {
                showErrorToast(error);
                setAutoRefresh(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleLoadMore = (additionalCount = 10) => {
        setIsLoadingMore(true);
        apiInstance.post(`enrichment-run-history/${runHistoryId}/load-more/`, {
            additional_count: additionalCount
        })
            .then((response) => {
                if (response?.data?.success) {
                    showToast(`Loading more companies... This may take a moment.`);
                    // Start auto-refresh to track progress
                    setAutoRefresh(true);
                } else {
                    showError(response?.data?.message || 'Failed to load more');
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsLoadingMore(false);
            });
    };

    useEffect(() => {
        fetchRunHistory();
    }, [runHistoryId]);

    useEffect(() => {
        let interval;
        if (autoRefresh && runHistory && !['completed', 'failed', 'no_new_companies'].includes(runHistory.status)) {
            interval = setInterval(() => {
                fetchRunHistory();
            }, 3000); // Refresh every 3 seconds
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, runHistory?.status]);

    // Animate processing steps for educational UX
    useEffect(() => {
        if (!runHistory || ['completed', 'failed', 'no_new_companies'].includes(runHistory.status)) {
            setProcessingStep(0);
            return;
        }
        
        const maxSteps = statusEducation[runHistory.status]?.steps?.length || 1;
        const stepInterval = setInterval(() => {
            setProcessingStep(prev => (prev + 1) % maxSteps);
        }, 2000); // Cycle through steps every 2 seconds
        
        return () => clearInterval(stepInterval);
    }, [runHistory?.status]);
    
    // Reset step when status changes
    useEffect(() => {
        setProcessingStep(0);
    }, [runHistory?.status]);

    const status = runHistory?.status || 'pending';
    const statusDetails = statusInfo[status] || statusInfo['pending'];

    if (isLoading) {
        const loadingContent = (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isDrawerMode ? '100%' : 'calc(100vh - 64px)' }}>
                <LinearProgress sx={{ width: '50%' }} />
            </Box>
        );
        
        if (isDrawerMode) {
            return loadingContent;
        }
        return (
            <Page title="AI Enrichment Progress | CRM">
                {loadingContent}
            </Page>
        );
    }

    const mainContent = (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: isDrawerMode ? '100%' : 'calc(100vh - 64px)',
            overflow: 'hidden'
        }}>
            {/* Compact Header */}
            <Box sx={{ 
                px: 2, 
                py: 1, 
                backgroundColor: '#fff',
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0
            }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton 
                            size="small" 
                            onClick={() => isDrawerMode && onClose ? onClose() : navigate('/prompt-library?tab=enrichment&subtab=history')}
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {runHistory?.title || runHistory?.enrichment_prompt?.title || 'Enrichment Run'}
                        </Typography>
                    </Stack>
                    <Chip
                        icon={statusDetails.icon}
                        label={statusDetails.label}
                        color={statusDetails.color}
                        size="small"
                    />
                </Stack>
                
                {/* Progress bar */}
                <LinearProgress 
                    variant="determinate" 
                    value={statusDetails.progress}
                    sx={{ height: 4, borderRadius: 2, mt: 1 }}
                    color={statusDetails.color === 'default' ? 'primary' : statusDetails.color}
                />
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {/* Alerts */}
                {status === 'failed' && runHistory?.error_message && (
                    <Alert severity="error" sx={{ mb: 2, py: 0 }}>
                        {runHistory.error_message}
                    </Alert>
                )}
                {autoRefresh && !['completed', 'failed', 'no_new_companies'].includes(status) && (
                    <ProcessingStepsIndicator 
                        status={status} 
                        currentStep={processingStep} 
                        progress={runHistory?.parsed_apollo_params?.progress}
                    />
                )}

                {/* Stats Row - Inline Compact */}
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                    <Chip 
                        label={`${runHistory?.requested_count || 0} Requested`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip 
                        label={`${runHistory?.parsed_apollo_params?.progress?.companies_created || runHistory?.actual_count || 0} Created`} 
                        size="small" 
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip 
                        label={`${runHistory?.pitches_generated || 0} Pitches`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                </Stack>
                
                {/* Load More Section - Show when pagination available (for completed or failed) */}
                {['completed', 'failed', 'no_new_companies'].includes(runHistory?.status) && (
                    <Box sx={{ 
                        p: 1, 
                        mb: 1.5, 
                        bgcolor: runHistory?.parsed_apollo_params?.pagination_state?.has_more ? 'rgba(25, 118, 210, 0.04)' : 'rgba(76, 175, 80, 0.04)', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: runHistory?.parsed_apollo_params?.pagination_state?.has_more ? 'primary.light' : 'success.light'
                    }}>
                        {runHistory?.parsed_apollo_params?.pagination_state ? (
                            // New campaigns with pagination state
                            runHistory?.parsed_apollo_params?.pagination_state?.has_more ? (
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleLoadMore(10)}
                                        disabled={isLoadingMore || runHistory?.status === 'creating'}
                                        startIcon={isLoadingMore ? <CircularProgress size={14} color="inherit" /> : null}
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        {isLoadingMore ? 'Loading...' : 'Load 10 More Companies'}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Checked page {runHistory?.parsed_apollo_params?.pagination_state?.current_page || 1} of {runHistory?.parsed_apollo_params?.pagination_state?.total_pages || 1}
                                        {' '}• {runHistory?.parsed_apollo_params?.pagination_state?.total_entries || 0} total available in Apollo
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="success.main">
                                    ✓ All {runHistory?.parsed_apollo_params?.pagination_state?.total_entries || 0} companies from Apollo have been checked
                                </Typography>
                            )
                        ) : (
                            // Old campaigns without pagination state
                            <Typography variant="caption" color="text.secondary">
                                ℹ️ This campaign was run before the "Load More" feature was added. Run a new campaign to use this feature.
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Run Info - Compact Grid */}
                <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1, 
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                }}>
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Country</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {runHistory?.effective_country?.label || runHistory?.enrichment_prompt?.country || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Triggered By</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {runHistory?.triggered_by ? `${runHistory.triggered_by.first_name}` : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Date & Time</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {runHistory?.started_at ? `${getDDMMYYYYFormat(runHistory.started_at)} ${new Date(runHistory.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Created Companies - List with Mark button */}
                {runHistory?.companies_created && runHistory.companies_created.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                            Created Companies ({runHistory.companies_created.length})
                        </Typography>
                        <Stack spacing={0.5}>
                            {runHistory.companies_created.map((companyId) => {
                                const company = companyDetails[companyId];
                                const isLoading = loadingCompanies[companyId];
                                const isSelected = selectedCompanyId === companyId;
                                const isOutreached = company?.is_outreached;
                                
                                return (
                                    <Paper
                                        key={companyId}
                                        onClick={() => handleOpenCompanyDetail(companyId)}
                                        sx={{
                                            p: 0.75,
                                            cursor: 'pointer',
                                            border: isSelected ? '2px solid #00AB55' : '1px solid #e0e0e0',
                                            bgcolor: isSelected ? 'rgba(0, 171, 85, 0.08)' : 'transparent',
                                            '&:hover': { bgcolor: 'rgba(0, 171, 85, 0.04)', borderColor: '#00AB55' }
                                        }}
                                    >
                                        {isLoading ? (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CircularProgress size={12} />
                                                <Typography variant="caption">Loading #{companyId}...</Typography>
                                            </Stack>
                                        ) : company?.error ? (
                                            <Chip label={`#${companyId}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        ) : company ? (
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                                                    <Chip 
                                                        label={`#${companyId}`} 
                                                        size="small" 
                                                        color={isSelected ? "success" : "primary"}
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.6rem', minWidth: 40 }} 
                                                    />
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            fontWeight={500}
                                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                                                        >
                                                            {company.company_name || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                            {company.country?.label} {company.apollo_enrichment_data?.organization?.industry && `• ${company.apollo_enrichment_data.organization.industry}`}
                                                            {company.created && ` • ${new Date(company.created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${new Date(company.created).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" alignItems="center" spacing={0.25}>
                                                    {isOutreached ? (
                                                        <Tooltip title="View Outreach Details">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenMarkOutreach(companyId, e);
                                                                }}
                                                                sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                            >
                                                                ✅ Outreached
                                                            </Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenMarkOutreach(companyId, e);
                                                                }}
                                                                sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                            >
                                                                Mark
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenMarkInvalid(companyId, e);
                                                                }}
                                                                disabled={isMarkingInvalid}
                                                                sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                            >
                                                                Invalid
                                                            </Button>
                                                        </>
                                                    )}
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleOpenCompanyDetail(companyId)}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <Visibility sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Chip label={`#${companyId}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        )}
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Box>
                )}

                {/* Rejected Companies Section with 3 Tabs */}
                {(rejectedStats?.total > 0 ||
                  runHistory?.parsed_apollo_params?.industry_rejected_count > 0 || 
                  runHistory?.parsed_apollo_params?.duplicates_skipped > 0 ||
                  runHistory?.parsed_apollo_params?.business_type_rejected_count > 0 ||
                  runHistory?.status === 'running') && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            size="small"
                            variant={showRejected ? "contained" : "outlined"}
                            color="error"
                            onClick={() => setShowRejected(!showRejected)}
                            startIcon={showRejected ? <ExpandLess /> : <ExpandMore />}
                            sx={{ mb: 1, fontSize: '0.7rem' }}
                        >
                            {showRejected ? 'Hide' : 'Show'} Rejected Companies ({
                                rejectedStats?.total || (
                                    (runHistory?.parsed_apollo_params?.industry_rejected_count || 0) +
                                    (runHistory?.parsed_apollo_params?.duplicates_skipped || 0) +
                                    (runHistory?.parsed_apollo_params?.business_type_rejected_count || 0)
                                )
                            })
                        </Button>
                        
                        {showRejected && (
                            <Box sx={{ border: '1px solid #ffcdd2', borderRadius: 1, overflow: 'hidden' }}>
                                {/* Search bar */}
                                <Box sx={{ p: 1, bgcolor: 'rgba(244, 67, 54, 0.04)', borderBottom: '1px solid #ffcdd2' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search company name or website..."
                                        value={rejectedSearch}
                                        onChange={(e) => setRejectedSearch(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                            sx: { fontSize: '0.75rem', height: 32 }
                                        }}
                                    />
                                </Box>
                                
                                {/* Two tabs: Duplicates and Others */}
                                <Tabs
                                    value={rejectedTab}
                                    onChange={(e, newValue) => {
                                        setRejectedTab(newValue);
                                        if (newValue === 0) setDuplicateFilter('all');
                                        if (newValue === 1) setRejectedFilter('all');
                                    }}
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 32,
                                        bgcolor: 'rgba(244, 67, 54, 0.08)',
                                        '& .MuiTab-root': {
                                            minHeight: 32,
                                            fontSize: '0.7rem',
                                            textTransform: 'none',
                                            py: 0.5
                                        }
                                    }}
                                >
                                    <Tab 
                                        label={`Duplicates (${
                                            (rejectedStats?.by_reason?.duplicate?.count || 0) +
                                            (rejectedStats?.by_reason?.duplicate_marked_invalid?.count || 0) +
                                            (rejectedStats?.by_reason?.duplicate_outreached?.count || 0) +
                                            (rejectedStats?.by_reason?.duplicate_deactivated?.count || 0)
                                        })`}
                                        sx={{ color: rejectedTab === 0 ? 'warning.main' : 'text.secondary' }}
                                    />
                                    <Tab 
                                        label={`Validation Failed (${
                                            (rejectedStats?.by_reason?.no_website?.count || 0) +
                                            (rejectedStats?.by_reason?.website_not_working?.count || 0) +
                                            (rejectedStats?.by_reason?.business_type_mismatch?.count || 0) +
                                            (rejectedStats?.by_reason?.low_confidence?.count || 0) +
                                            (rejectedStats?.by_reason?.insufficient_evidence?.count || 0) +
                                            (rejectedStats?.by_reason?.employee_size_mismatch?.count || 0)
                                        })`}
                                        sx={{ color: rejectedTab === 1 ? 'error.main' : 'text.secondary' }}
                                    />
                                </Tabs>
                                
                                {/* Dropdown filter for Duplicates tab */}
                                {rejectedTab === 0 && (
                                    <Box sx={{ p: 1, bgcolor: 'rgba(255, 152, 0, 0.04)', borderBottom: '1px solid #ffe0b2' }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={duplicateFilter}
                                                onChange={(e) => setDuplicateFilter(e.target.value)}
                                                sx={{ fontSize: '0.7rem', height: 28 }}
                                            >
                                                <MenuItem value="all">All Duplicates ({
                                                    (rejectedStats?.by_reason?.duplicate?.count || 0) +
                                                    (rejectedStats?.by_reason?.duplicate_marked_invalid?.count || 0) +
                                                    (rejectedStats?.by_reason?.duplicate_outreached?.count || 0) +
                                                    (rejectedStats?.by_reason?.duplicate_deactivated?.count || 0)
                                                })</MenuItem>
                                                <MenuItem value="duplicate">Regular Duplicates ({rejectedStats?.by_reason?.duplicate?.count || 0})</MenuItem>
                                                <MenuItem value="duplicate_outreached">Already Outreached ({rejectedStats?.by_reason?.duplicate_outreached?.count || 0})</MenuItem>
                                                <MenuItem value="duplicate_marked_invalid">Marked Invalid ({rejectedStats?.by_reason?.duplicate_marked_invalid?.count || 0})</MenuItem>
                                                <MenuItem value="duplicate_deactivated">Deactivated ({rejectedStats?.by_reason?.duplicate_deactivated?.count || 0})</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                                
                                {/* Dropdown filter for Validation Failed tab */}
                                {rejectedTab === 1 && (
                                    <Box sx={{ p: 1, bgcolor: 'rgba(244, 67, 54, 0.04)', borderBottom: '1px solid #ffcdd2' }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={rejectedFilter}
                                                onChange={(e) => setRejectedFilter(e.target.value)}
                                                sx={{ fontSize: '0.7rem', height: 28 }}
                                            >
                                                <MenuItem value="all">All Validation Failures ({
                                                    (rejectedStats?.by_reason?.no_website?.count || 0) +
                                                    (rejectedStats?.by_reason?.website_not_working?.count || 0) +
                                                    (rejectedStats?.by_reason?.business_type_mismatch?.count || 0) +
                                                    (rejectedStats?.by_reason?.low_confidence?.count || 0) +
                                                    (rejectedStats?.by_reason?.insufficient_evidence?.count || 0) +
                                                    (rejectedStats?.by_reason?.employee_size_mismatch?.count || 0)
                                                })</MenuItem>
                                                <MenuItem value="business_type_mismatch">Not Software/Digital ({rejectedStats?.by_reason?.business_type_mismatch?.count || 0})</MenuItem>
                                                <MenuItem value="low_confidence">Low Confidence ({rejectedStats?.by_reason?.low_confidence?.count || 0})</MenuItem>
                                                <MenuItem value="insufficient_evidence">Website Unclear ({rejectedStats?.by_reason?.insufficient_evidence?.count || 0})</MenuItem>
                                                <MenuItem value="employee_size_mismatch">Wrong Employee Size ({rejectedStats?.by_reason?.employee_size_mismatch?.count || 0})</MenuItem>
                                                <MenuItem value="no_website">No Website ({rejectedStats?.by_reason?.no_website?.count || 0})</MenuItem>
                                                <MenuItem value="website_not_working">Website Not Working ({rejectedStats?.by_reason?.website_not_working?.count || 0})</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                                
                                <Box sx={{ maxHeight: 280, overflow: 'auto', p: 1 }}>
                                    {loadingRejected ? (
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="caption">Loading rejected companies...</Typography>
                                        </Stack>
                                    ) : (() => {
                                        // Color scheme based on rejection reason
                                        const getReasonColor = (reason) => {
                                            const colorMap = {
                                                'duplicate': { border: '#ffe0b2', bg: 'rgba(255, 152, 0, 0.04)', chip: 'warning' },
                                                'duplicate_marked_invalid': { border: '#ffe0b2', bg: 'rgba(255, 152, 0, 0.04)', chip: 'warning' },
                                                'duplicate_outreached': { border: '#c8e6c9', bg: 'rgba(76, 175, 80, 0.04)', chip: 'success' },
                                                'duplicate_deactivated': { border: '#e0e0e0', bg: 'rgba(158, 158, 158, 0.04)', chip: 'default' },
                                                'no_website': { border: '#ffcdd2', bg: 'rgba(244, 67, 54, 0.04)', chip: 'error' },
                                                'website_not_working': { border: '#ffcdd2', bg: 'rgba(244, 67, 54, 0.04)', chip: 'error' },
                                                'business_type_mismatch': { border: '#bbdefb', bg: 'rgba(33, 150, 243, 0.04)', chip: 'info' },
                                                'low_confidence': { border: '#fff9c4', bg: 'rgba(255, 235, 59, 0.04)', chip: 'warning' },
                                                'insufficient_evidence': { border: '#ffccbc', bg: 'rgba(255, 87, 34, 0.04)', chip: 'warning' },
                                                'employee_size_mismatch': { border: '#e1bee7', bg: 'rgba(156, 39, 176, 0.04)', chip: 'secondary' },
                                            };
                                            return colorMap[reason] || { border: '#e0e0e0', bg: 'rgba(158, 158, 158, 0.04)', chip: 'default' };
                                        };
                                        
                                        // Get short reason label
                                        const getShortReasonLabel = (reason) => {
                                            const labelMap = {
                                                'duplicate': 'Duplicate',
                                                'duplicate_marked_invalid': 'Duplicate (Invalid)',
                                                'duplicate_outreached': 'Duplicate (Outreached)',
                                                'duplicate_deactivated': 'Duplicate (Deactivated)',
                                                'no_website': 'No Website',
                                                'website_not_working': 'Website Error',
                                                'business_type_mismatch': 'Not Software',
                                                'low_confidence': 'Low Confidence',
                                                'insufficient_evidence': 'Unclear',
                                                'employee_size_mismatch': 'Wrong Size',
                                            };
                                            return labelMap[reason] || reason;
                                        };
                                        
                                        // Render a single company card
                                        const renderCompanyCard = (company, index) => {
                                            const isSelected = selectedRejectedCompany?.id === company.id;
                                            const colors = getReasonColor(company.rejection_reason);
                                            const isDuplicate = company.rejection_reason?.startsWith('duplicate');
                                            
                                            return (
                                                <Paper
                                                    key={company.id}
                                                    onClick={() => handleViewRejectedCompany(company)}
                                                    sx={{
                                                        p: 0.75,
                                                        cursor: 'pointer',
                                                        border: isSelected ? `3px solid #1976d2` : `1px solid ${colors.border}`,
                                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.12)' : colors.bg,
                                                        boxShadow: isSelected ? '0 0 8px rgba(25, 118, 210, 0.4)' : 'none',
                                                        transform: isSelected ? 'scale(1.01)' : 'none',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': { bgcolor: isSelected ? 'rgba(25, 118, 210, 0.15)' : colors.bg, opacity: 0.9 }
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography variant="caption" fontWeight={500} sx={{ display: 'block' }}>
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                                                    {index + 1}.
                                                                </Typography>
                                                                {company.company_name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                                                                {company.company_website || 'No website'}
                                                            </Typography>
                                                            {/* Show rejection details inline - not the chip for duplicates tab since we have sections */}
                                                            {rejectedTab !== 0 && (
                                                                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                                                                    <Chip 
                                                                        label={getShortReasonLabel(company.rejection_reason)} 
                                                                        size="small" 
                                                                        color={colors.chip}
                                                                        variant="outlined"
                                                                        sx={{ height: 16, fontSize: '0.55rem' }} 
                                                                    />
                                                                    {company.llm_confidence > 0 && (
                                                                        <Typography variant="caption" color={`${colors.chip}.main`} sx={{ fontSize: '0.55rem' }}>
                                                                            {company.llm_confidence}%
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            )}
                                                            {company.rejection_details && (
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary" 
                                                                    sx={{ 
                                                                        fontSize: '0.55rem', 
                                                                        display: 'block', 
                                                                        mt: 0.25,
                                                                        fontStyle: 'italic',
                                                                        maxWidth: '100%',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {company.rejection_details.length > 80 
                                                                        ? company.rejection_details.substring(0, 80) + '...' 
                                                                        : company.rejection_details}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            {/* Only show Mark/Invalid for regular duplicates */}
                                                            {company.rejection_reason === 'duplicate' && company.existing_company_id && (
                                                                companyDetails[company.existing_company_id]?.is_outreached ? (
                                                                    <Tooltip title="View Outreach Details">
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            color="success"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenMarkOutreach(company.existing_company_id, e);
                                                                            }}
                                                                            sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                                        >
                                                                            ✅ Outreached
                                                                        </Button>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            color="success"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenMarkOutreach(company.existing_company_id, e);
                                                                            }}
                                                                            sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                                        >
                                                                            Mark
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="warning"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenMarkInvalid(company.existing_company_id, e);
                                                                            }}
                                                                            disabled={isMarkingInvalid}
                                                                            sx={{ minWidth: 45, height: 20, fontSize: '0.6rem', px: 0.5 }}
                                                                        >
                                                                            Invalid
                                                                        </Button>
                                                                    </>
                                                                )
                                                            )}
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={(e) => handleViewRejectedCompany(company, e)}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <Visibility sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Stack>
                                                    </Stack>
                                                </Paper>
                                            );
                                        };
                                        
                                        // Filter by search term
                                        const searchTerm = rejectedSearch.toLowerCase().trim();
                                        const filteredCompanies = searchTerm 
                                            ? rejectedCompanies.filter(c => 
                                                c.company_name?.toLowerCase().includes(searchTerm) ||
                                                c.company_website?.toLowerCase().includes(searchTerm)
                                            )
                                            : rejectedCompanies;
                                        
                                        // Render flat list for both tabs
                                        return (
                                            <>
                                                {searchTerm && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                                                        Showing {filteredCompanies.length} of {rejectedCompanies.length} companies
                                                    </Typography>
                                                )}
                                                {filteredCompanies.length > 0 ? (
                                                    <Stack spacing={0.5}>
                                                        {filteredCompanies.map((company, index) => renderCompanyCard(company, index))}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                                                        {searchTerm 
                                                            ? `No companies matching "${rejectedSearch}"` 
                                                            : `No ${rejectedTab === 0 ? 'duplicate' : 'validation failed'} companies`}
                                                    </Typography>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Search Prompt - Plain Text */}
                {runHistory?.enrichment_prompt?.prompt && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="primary.main" fontWeight={600} display="block">
                            Search Prompt: {runHistory?.enrichment_prompt?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', lineHeight: 1.4 }}>
                            {runHistory.enrichment_prompt.prompt}
                        </Typography>
                    </Box>
                )}

                {/* Pitch Template - Plain Text */}
                {runHistory?.prompt_template?.prompt && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="warning.main" fontWeight={600} display="block">
                            Pitch Template: {runHistory?.prompt_template?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', lineHeight: 1.4 }}>
                            {runHistory.prompt_template.prompt}
                        </Typography>
                    </Box>
                )}

                {/* Parsed Apollo Parameters - Plain Text */}
                {runHistory?.parsed_apollo_params && Object.keys(runHistory.parsed_apollo_params).length > 0 && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                            Apollo Parameters
                        </Typography>
                        <Typography 
                            component="pre" 
                            sx={{ 
                                fontSize: '0.65rem',
                                color: 'text.secondary',
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {JSON.stringify(runHistory.parsed_apollo_params, null, 2)}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    // Full content with side-by-side company detail OR mark outreach panel OR rejected company view OR invalid panel
    const isExpanded = !!selectedCompanyId || markOutreachPanelOpen || !!selectedRejectedCompany || markInvalidPanelOpen;
    
    const fullContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row' }}>
            {/* Left Panel - Progress Content (fixed width matching original drawer) */}
            <Box sx={{ 
                width: isExpanded ? '40%' : '100%',
                minWidth: isExpanded ? 320 : 'auto',
                borderRight: isExpanded ? '1px solid #e0e0e0' : 'none',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {mainContent}
            </Box>
            
            {/* Right Panel - Company Detail (only shows when company selected) */}
            {selectedCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa' }}>
                    <AddCompany 
                        id={selectedCompanyId} 
                        setOpenDrawer={handleCloseCompanyDetail}
                        defaultTab="pitch"
                    />
                </Box>
            )}
            
            {/* Right Panel - Mark Outreach Form or Details (only shows when mark panel is open) */}
            {markOutreachPanelOpen && outreachCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '1.5rem' }}>📤</span>
                            <Typography variant="h6">
                                {existingOutreachDetails ? 'Outreach Details' : 'Mark as Outreached'}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseMarkOutreach} size="small">
                            <ArrowBack />
                        </IconButton>
                    </Box>
                    
                    {/* Company Info */}
                    {companyDetails[outreachCompanyId] && (
                        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(0, 171, 85, 0.08)', border: '1px solid #00AB55' }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {companyDetails[outreachCompanyId].company_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                #{outreachCompanyId} • {companyDetails[outreachCompanyId].country?.label}
                            </Typography>
                        </Paper>
                    )}
                    
                    {/* Loading state */}
                    {loadingOutreachDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : existingOutreachDetails ? (
                        /* Show existing outreach details */
                        <Stack spacing={2}>
                            <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                                    ✅ Already Outreached
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Method</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.outreach_method_display || existingOutreachDetails.outreach_method}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Outreached At</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.outreached_at ? 
                                                new Date(existingOutreachDetails.outreached_at).toLocaleString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Marked By</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.marked_by_name || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Comment</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {existingOutreachDetails.comment || 'No comment'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                            <Button onClick={handleCloseMarkOutreach} variant="outlined">
                                Close
                            </Button>
                        </Stack>
                    ) : (
                        /* Show form to mark as outreached */
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Outreach Method *
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={outreachMethod}
                                        onChange={(e) => setOutreachMethod(e.target.value)}
                                    >
                                        <MenuItem value="email">📧 Email</MenuItem>
                                        <MenuItem value="call">📞 Call</MenuItem>
                                        <MenuItem value="linkedin">💼 LinkedIn</MenuItem>
                                        <MenuItem value="other">📝 Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Stack direction="row" spacing={1}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Date *
                                    </Typography>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        value={outreachDate}
                                        onChange={(e) => setOutreachDate(e.target.value)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Time *
                                    </Typography>
                                    <TextField
                                        type="time"
                                        fullWidth
                                        size="small"
                                        value={outreachTime}
                                        onChange={(e) => setOutreachTime(e.target.value)}
                                    />
                                </Box>
                            </Stack>
                            
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Comment *
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    placeholder="Enter outreach details..."
                                    value={outreachComment}
                                    onChange={(e) => setOutreachComment(e.target.value)}
                                />
                            </Box>
                            
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button onClick={handleCloseMarkOutreach} disabled={isMarkingOutreach}>
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="success"
                                    onClick={handleSubmitMarkOutreach}
                                    disabled={isMarkingOutreach}
                                >
                                    {isMarkingOutreach ? 'Saving...' : 'Mark as Outreached'}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            )}
            
            {/* Right Panel - Mark Invalid Form (only shows when invalid panel is open) */}
            {markInvalidPanelOpen && invalidCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
                    {/* Header - Orange like EditJourney */}
                    <Box sx={{ 
                        bgcolor: '#ff9800', 
                        color: 'white', 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between' 
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>⚠️</span>
                            <Typography variant="h6">Mark as Invalid</Typography>
                        </Box>
                        <IconButton onClick={handleCloseMarkInvalid} sx={{ color: 'white' }}>
                            <ArrowBack />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                        {companyDetails[invalidCompanyId] && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                                <Typography variant="h6">{companyDetails[invalidCompanyId]?.company_name}</Typography>
                            </Box>
                        )}
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reason *</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedInvalidReason}
                                    onChange={(e) => setSelectedInvalidReason(e.target.value)}
                                    displayEmpty
                                    disabled={loadingInvalidReasons}
                                >
                                    <MenuItem value="" disabled>
                                        {loadingInvalidReasons ? 'Loading reasons...' : 'Select a reason'}
                                    </MenuItem>
                                    {invalidReasons.map((reason) => (
                                        <MenuItem key={reason.id} value={reason.id}>
                                            {reason.label || reason.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Additional Details (Optional)</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Enter additional details about why this company is invalid..."
                                value={invalidDescription}
                                onChange={(e) => setInvalidDescription(e.target.value)}
                            />
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                            <Typography variant="caption" color="warning.dark">
                                ⚠️ This company will be moved to the 5% stage and marked as invalid.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseMarkInvalid} disabled={isMarkingInvalid}>
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            color="warning"
                            onClick={handleSubmitMarkInvalid}
                            disabled={isMarkingInvalid || !selectedInvalidReason}
                            startIcon={isMarkingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isMarkingInvalid ? 'Saving...' : 'Mark as Invalid'}
                        </Button>
                    </Box>
                </Box>
            )}
            
            {/* Right Panel - Rejected Company Details (only shows when rejected company selected) */}
            {selectedRejectedCompany && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa' }}>
                    {/* For duplicates: show loading, AddCompany, or not found */}
                    {selectedRejectedCompany.rejection_reason === 'duplicate' && searchingDuplicate ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Searching for existing company...
                            </Typography>
                        </Box>
                    ) : selectedRejectedCompany.rejection_reason === 'duplicate' && duplicateCompanyId ? (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Action buttons for duplicate company */}
                            <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={(e) => {
                                            handleCloseRejectedCompany();
                                            handleOpenMarkOutreach(duplicateCompanyId, e);
                                        }}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Mark as Outreach
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={(e) => handleOpenMarkInvalid(duplicateCompanyId, e)}
                                        disabled={isMarkingInvalid}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Company Invalid
                                    </Button>
                                </Stack>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                <AddCompany 
                                    key={`duplicate-${duplicateCompanyId}`}
                                    id={duplicateCompanyId} 
                                    setOpenDrawer={handleCloseRejectedCompany}
                                    defaultTab="pitch"
                                />
                            </Box>
                        </Box>
                    ) : selectedRejectedCompany.rejection_reason === 'duplicate' && !duplicateCompanyId ? (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                    <Typography variant="h6">Duplicate Company</Typography>
                                </Box>
                                <IconButton onClick={handleCloseRejectedCompany} size="small">
                                    <ArrowBack />
                                </IconButton>
                            </Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Could not find the existing company. It may have been deleted or renamed.
                            </Alert>
                            <Paper sx={{ p: 1.5, bgcolor: 'rgba(255, 152, 0, 0.08)', border: '1px solid #ffe0b2' }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {selectedRejectedCompany.company_name}
                                </Typography>
                                {selectedRejectedCompany.company_website && (
                                    <Typography variant="caption" color="primary" component="a" href={selectedRejectedCompany.company_website} target="_blank">
                                        {selectedRejectedCompany.company_website}
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    ) : (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span style={{ fontSize: '1.5rem' }}>🚫</span>
                                    <Typography variant="h6">Rejected Company Details</Typography>
                                </Box>
                                <IconButton onClick={handleCloseRejectedCompany} size="small">
                                    <ArrowBack />
                                </IconButton>
                            </Box>
                            
                            {/* Company Header */}
                            <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(244, 67, 54, 0.08)', border: '1px solid #ffcdd2' }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {selectedRejectedCompany.company_name}
                                </Typography>
                                {selectedRejectedCompany.company_website && (
                                    <Typography 
                                        variant="caption" 
                                        color="primary" 
                                        component="a" 
                                        href={selectedRejectedCompany.company_website} 
                                        target="_blank"
                                        sx={{ display: 'block', mb: 0.5 }}
                                    >
                                        {selectedRejectedCompany.company_website}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                    <Chip 
                                        label={selectedRejectedCompany.rejection_reason_display} 
                                        size="small" 
                                        color="error"
                                        sx={{ height: 20, fontSize: '0.7rem' }} 
                                    />
                                    {selectedRejectedCompany.llm_confidence > 0 && (
                                        <Chip 
                                            label={`${selectedRejectedCompany.llm_confidence}% confidence`} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }} 
                                        />
                                    )}
                                </Stack>
                            </Paper>
                            
                            {/* Rejection Details */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                    Rejection Details
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Target Industry</Typography>
                                        <Typography variant="body2">{selectedRejectedCompany.target_industry || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">Detected Industry</Typography>
                                        <Typography variant="body2">{selectedRejectedCompany.detected_industry || selectedRejectedCompany.apollo_industry || '-'}</Typography>
                                    </Grid>
                                </Grid>
                                {selectedRejectedCompany.rejection_details && (
                                    <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(244, 67, 54, 0.04)', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                                        <Typography variant="caption" color="error.dark" sx={{ fontStyle: 'italic' }}>
                                            {selectedRejectedCompany.rejection_details}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                    
                            {/* Apollo Data */}
                            {selectedRejectedCompany.apollo_data && Object.keys(selectedRejectedCompany.apollo_data).length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                        Apollo Data
                                    </Typography>
                                    {(() => {
                                        const apolloOrg = selectedRejectedCompany.apollo_data.organization || selectedRejectedCompany.apollo_data;
                                        return (
                                            <Stack spacing={1.5}>
                                                {apolloOrg.industry && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Industry</Typography>
                                                        <Typography variant="body2">{apolloOrg.industry}</Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.estimated_num_employees && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Employees</Typography>
                                                        <Typography variant="body2">{apolloOrg.estimated_num_employees?.toLocaleString()}</Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.annual_revenue && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Annual Revenue</Typography>
                                                        <Typography variant="body2">${(apolloOrg.annual_revenue / 1000000).toFixed(1)}M</Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.founded_year && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Founded</Typography>
                                                        <Typography variant="body2">{apolloOrg.founded_year}</Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.linkedin_url && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">LinkedIn</Typography>
                                                        <Typography 
                                                            variant="body2" 
                                                            component="a" 
                                                            href={apolloOrg.linkedin_url} 
                                                            target="_blank"
                                                            color="primary"
                                                        >
                                                            View Profile
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.short_description && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{apolloOrg.short_description}</Typography>
                                                    </Box>
                                                )}
                                                {apolloOrg.keywords && apolloOrg.keywords.length > 0 && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">Keywords</Typography>
                                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                                            {apolloOrg.keywords.slice(0, 10).map((kw, idx) => (
                                                                <Chip key={idx} label={kw} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}
                                                
                                                {/* Raw Apollo Data (collapsible) */}
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                        Raw Apollo Data
                                                    </Typography>
                                                    <Box sx={{ 
                                                        maxHeight: 200, 
                                                        overflow: 'auto', 
                                                        bgcolor: '#f5f5f5', 
                                                        p: 1, 
                                                        borderRadius: 1,
                                                        border: '1px solid #e0e0e0'
                                                    }}>
                                                        <Typography 
                                                            component="pre" 
                                                            sx={{ 
                                                                fontSize: '0.6rem', 
                                                                m: 0, 
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {JSON.stringify(apolloOrg, null, 2)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );

    // Return with or without Page wrapper based on mode
    if (isDrawerMode) {
        return fullContent;
    }
    
    return (
        <Page title="AI Enrichment Progress | CRM">
            {fullContent}
        </Page>
    );
}
